/**
 * Defect ids are only guaranteed to be unique within an inspection - they are
 * created by hand during inspections, so two vessels can legitimately carry the
 * same defect id. Anything that looks a defect up, keys it or merges it must
 * therefore use the inspection guid alongside the defect id.
 *
 * Kept free of service/store imports so it can be used from reducers.
 */

/** Anything that can identify a defect. The inspection guid may be absent on
 * legacy links, in which case matching falls back to the defect id alone. */
export interface DefectRef {
    defectId: number | string;
    inspectionId?: string;
}

export type IdentifiableDefect = { defectId: number; inspectionId: string };

/** Unique key for a defect - a defect id is only unique within an inspection. */
export const defectKey = (defect: DefectRef) =>
    `${defect.inspectionId}:${defect.defectId}`;

/** True when the defect is the one the reference points at. */
export const isSameDefect = (defect: IdentifiableDefect, ref: DefectRef) =>
    defect.defectId?.toString() === ref.defectId?.toString() &&
    (!ref.inspectionId || defect.inspectionId === ref.inspectionId);

/**
 * Finds the defect matching the given reference. When the reference carries an
 * inspection guid both must match, otherwise the defect id alone is used.
 */
export const findDefect = <T extends IdentifiableDefect>(
    defects: T[] | undefined,
    ref: DefectRef
): T | undefined => {
    if (!defects?.length) {
        return undefined;
    }

    return defects.find((defect) => isSameDefect(defect, ref));
};
